import { hasCorrectFloatFormat, shouldSkipFormatting } from 'src/utilities/dashboard';
import currencyFormatter from 'currency-formatter';
import { always, concat, defaultTo, dropLast, equals, gt, ifElse, isEmpty, last, length, pipe, slice, split, startsWith } from 'ramda';

const PRECISION_POINT = 2
const hasDecimalPoints = (val: string = "") =>{
  const splitValuesArray = pipe(
    defaultTo(''),
    split('.') 
  )(val)
  return gt(length(splitValuesArray ), 1)
}


export const formatCurrencyFn = (value: any) => {
  return _formatCurrencyFn(value, 0, '$');
};

export const formatProposedPaymentFn = (value: any) => {
  const valueString = String(value)
  // Check if the value contains any English alphabets
  // This will handle the default proposed payment case
  const hasAlphabets = /[a-zA-Z]/.test(valueString);
  if (hasAlphabets) { return valueString }

  // Else, format the currency string with precision
  return formatCurrencyWithPrecisionHandler(value, PRECISION_POINT, "$")
}

export const formatCurrencyWithPrecisionFn = (value: any) => {
  return formatCurrencyWithPrecisionHandler(value, PRECISION_POINT, "$")
}

export const commaSeperatedFormatter = (value: any) => {
  return _formatCurrencyFn(value, 0, '');
};

export const formatRoundedCurrencyFn = (value: any, precision: number) => {
  return _formatCurrencyFn(value, precision);
};
export const formartContractCurrencyFn = (value: any, precision: number) => {
  return currencyFormatter.format(value, { code: 'USD', precision });
};
export const unFormatCurrencyFn = (value: any) => {
  return String(unFormatCurrencyFnNum(value));
};
export const unFormatCurrencyInCentsFnNum = (value: any) => {
  return parseFloat(Number(currencyFormatter.unformat(value, { code: 'USD', precision: 2 })).toFixed(2));
};
export const unFormatCurrencyFnNum = (value: any) => {
  return Number(currencyFormatter.unformat(value, { code: 'USD' }));
};
export const unFormatCurrentPrecisionFn = ( rawvalue:any) => {
  let value = rawvalue
  // NOTE: To trim of trailing period if any
  if (equals(last(value), ".")){
    value = dropLast(1,value)
  }
  const data = currencyFormatter.unformat(value, { code: 'USD', precision:PRECISION_POINT })
  return String(data)
}
export const unFormatCurrencyFnWithEmptyStringSupport = (value:any) => {
  if (equals(length(value),0)){
    return String(value)
  }
  return String(unFormatCurrencyFnNum(value));
}

const _formatCurrencyFn = (value: any, precision: number, symbol?: string) => {
  const unformatted = String(
    currencyFormatter.unformat(value, { code: 'USD' })
  );
  if (!hasCorrectFloatFormat(unformatted) || value === '') {
    return currencyFormatter.format(value, {
      code: 'USD',
      symbol,
      precision,
    });
  }
  return undefined;
};
// INFO: Handle case when there is no decimal point
const getCurrencyFormattedWithoutPrecision = (value: string, symbol?: string) => {
  const unformatted = currencyFormatter.unformat(value, { code: 'USD' })
  return currencyFormatter.format(unformatted, {
    code: 'USD',
    symbol,
    precision: 0,
  })
}

// INFO: Handle case when there is a decimal point
const getCurrencyFormattedWithPrecision = (value: string, precision: number, symbol: string) => {
  const { skip, val } = shouldSkipFormatting(value)

  // INFO: We just return the value as it is when decimal values are insignificant
  // such as 2.0, 2., 2.00
  if (skip) {
    const splitValuesArray = pipe(
      defaultTo(''),
      split('.') 
    )(value)
    const leadingNumbers = splitValuesArray[0]
    return concat(leadingNumbers, concat(".",slice(0, 2, val)))
  }

  //INFO: Keeping precision range from 0-precision value to avoid rigid digit place
  const adjustedPrecision = Math.min(val.length, precision)

  const unformatted = currencyFormatter.unformat(value, { code: 'USD', precision})
  return currencyFormatter.format(unformatted, {
    code: 'USD',
    symbol,
    precision: adjustedPrecision
  })

}

export const formatCurrencyWithPrecisionHandler = (rawvalue: any, precision: number, symbol: string) => {
  const value = String(rawvalue)

  return ifElse(
    hasDecimalPoints,
    always(getCurrencyFormattedWithPrecision(value, precision, symbol)),
    always(getCurrencyFormattedWithoutPrecision(value, symbol))
  )(value);
}

export const currencyFormatterWithOutSymbol = (value: any) => {
  return currencyFormatter.format(value, {
    symbol: '',
    precision: 2,
  });
};


export const integerStringToDecimalFormatter = (rawValue:  string | number) => {
  let value = String(rawValue);
  if (isEmpty(value)) {
    return '';
  }

  if (startsWith('$', value)) {
    value = unFormatCurrentPrecisionFn(value);
  }

  const decimalValue = parseFloat(value);
  if (isNaN(decimalValue)) {
    return '0.00';
  }
  return decimalValue.toFixed(2);
};
